import { useState, useCallback, useRef } from 'react';
import type { CustomFieldValue, CustomField, SelectOption } from './EditHistory.types';

export const useEditHistory = () =>
{
  const [listValues, setListValues] = useState<CustomFieldValue[]>([]);
  const [listCustomFields, setListCustomFields] = useState<CustomField[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [noData, setNoData] = useState(false);

  const updateRowRef = useRef(true);
  const cancelUpdateRef = useRef(false);
  const lastValueRef = useRef<any>(0);

  const addValue = useCallback((value: CustomFieldValue) =>
  {
    setListValues(prev => [...prev, value]);
  }, []);

  const updateCustomFieldValue = useCallback((index: number, customFieldValueId: number) =>
  {
    setListCustomFields(prev =>
    {
      const updated = [...prev];
      updated[index] = { ...updated[index]!, CustomFieldValueId: customFieldValueId } as any;
      return updated;
    });
  }, []);

  return {
    listValues,
    listCustomFields,
    isSaving,
    noData,
    updateRowRef,
    cancelUpdateRef,
    lastValueRef,
    setListValues,
    setListCustomFields,
    setIsSaving,
    setNoData,
    addValue,
    updateCustomFieldValue,
  };
};

export const useDateManagement = () =>
{
  const [selectMonth, setSelectMonth] = useState('0');
  const [selectYear, setSelectYear] = useState(new Date().getFullYear());

  const getMonthOptions = useCallback((): SelectOption[] =>
  {
    const allOptions: SelectOption[] = [
      { value: '0', label: 'January' },
      { value: '1', label: 'February' },
      { value: '2', label: 'March' },
      { value: '3', label: 'April' },
      { value: '4', label: 'May' },
      { value: '5', label: 'June' },
      { value: '6', label: 'July' },
      { value: '7', label: 'August' },
      { value: '8', label: 'September' },
      { value: '9', label: 'October' },
      { value: '10', label: 'November' },
      { value: '11', label: 'December' },
    ];

    const currentDate = new Date();
    if (currentDate.getFullYear() === selectYear)
    {
      return allOptions.slice(0, currentDate.getMonth() + 1);
    }
    return allOptions;
  }, [selectYear]);

  const getRangeDates = useCallback((from: string, to: string): string[] =>
  {
    const getDate = (date: Date): string =>
    {
      const m = date.getMonth();
      const d = date.getDate();
      return `${(m + 1).toString().padStart(2, '0')}/${d.toString().padStart(2, '0')}/${date.getFullYear()}`;
    };

    const fs = from.split('/');
    const startDate = new Date(parseInt(fs[2]!), parseInt(fs[0]!) - 1, parseInt(fs[1]!));
    const result = [getDate(startDate)];
    let start = startDate.getTime();

    const ts = to.split('/');
    const end = new Date(parseInt(ts[2]!), parseInt(ts[0]!) - 1, parseInt(ts[1]!)).getTime();

    while (start < end)
    {
      start += 86400000;
      startDate.setTime(start);
      result.push(getDate(startDate));
    }

    return result;
  }, []);

  const getIsToday = useCallback((date: string): boolean =>
  {
    const start = new Date();
    const today = `${(start.getMonth() + 1).toString().padStart(2, '0')}/${start.getDate().toString().padStart(2, '0')}/${start.getFullYear()}`;
    return today === date;
  }, []);

  const formatDate = useCallback((date: Date): string =>
  {
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`;
  }, []);

  return {
    selectMonth,
    selectYear,
    setSelectMonth,
    setSelectYear,
    getMonthOptions,
    getRangeDates,
    getIsToday,
    formatDate,
  };
};

export const useDashboard = () =>
{
  const [dashboards, setDashboards] = useState<SelectOption[]>([]);
  const [selectDashboard, setSelectDashboard] = useState('');
  const [showErrorDashboard, setShowErrorDashboard] = useState(false);

  const resetDashboardError = useCallback(() =>
  {
    setShowErrorDashboard(false);
  }, []);

  return {
    dashboards,
    selectDashboard,
    showErrorDashboard,
    setDashboards,
    setSelectDashboard,
    setShowErrorDashboard,
    resetDashboardError,
  };
};

export const useSpreadsheet = () =>
{
  const [componentHeight, setComponentHeight] = useState(200);
  const [componentWidth, setComponentWidth] = useState(300);
  const [options, setOptions] = useState<any>({
    columnDrag: false,
    data: [[]],
    minDimensions: [10, 10],
    contextMenu: false,
    hideIndex: true,
  });

  const wrapperRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<any>(null);

  const updateDimensions = useCallback((width: number, height: number) =>
  {
    setComponentWidth(width);
    setComponentHeight(height);
  }, []);

  return {
    componentHeight,
    componentWidth,
    options,
    wrapperRef,
    spreadsheetRef,
    setComponentHeight,
    setComponentWidth,
    setOptions,
    updateDimensions,
  };
};
